import { useMemo, useState } from 'react'

import { IconMoodSad } from '@tabler/icons-react'
import { Switch } from 'antd'
import { NextRouter, useRouter } from 'next/router'
import styled from 'styled-components'

import ContactList from '../../modules/subcontractors/Contacts/ContactList'
import {
  GET_STAFF_WORKERS,
  GET_WORKERS_FOR_SEARCH,
} from '../../modules/workers/graphQL'
import { TabContent, Tabs } from '../shared-components/BUI/Tabs'
import { Text } from '../shared-components/BUI/Text'
import { Flexbox } from '../shared-components/Flexbox'

import { AssigneePill } from '@/components/shared-components/AssigneePill'
import {
  Button,
  Props as ButtonProps,
} from '@/components/shared-components/BUI/Button'
import { Modal } from '@/components/shared-components/BUI/Modal'
import ServerSideListView from '@/components/shared-components/ServerSideListView'
import { useUser } from '@/context/userContext'
import { GET_ADMIN_USERS_FOR_SEARCH } from '@/modules/adminUsers/graphQL'
import {
  GET_BUILDER_SUBBIE_ASSIGNEES,
  GET_PROJECT_SUBBIES_ASSIGNEES,
} from '@/modules/subcontractors/graphQL'
import {
  getAssigneeName,
  getWorkerName,
  pluraliseWord,
  routes,
} from '@/utils/index'

import { buildPassTheme } from 'src/styles/theme'

import {
  AdminUsersForSearchUiQuery,
  AdminUsersForSearchUiQueryVariables,
  ArchivedFilter,
  AssigneeInput,
  AssigneeType,
  BuilderSubbieAssigneesUiQuery,
  BuilderSubbieAssigneesUiQueryVariables,
  BuilderWorkersUiQuery,
  BuilderWorkersUiQueryVariables,
  Contact,
  ProjectSubbieAssigneesUiQuery,
  ProjectSubbieAssigneesUiQueryVariables,
  Subcontractor,
  WorkersListForSearchUiQuery,
  WorkersListForSearchUiQueryVariables,
} from 'types/api.graphql'
import { AssigneeLike, ProjectLike } from 'types/vagueTypes'

enum Tab {
  AdminUsers = 'BuildPass users',
  Workers = 'Workers',
  Subbies = 'Companies',
  Staff = 'Staff',
}

const ContentWrapper = styled.div`
  padding: 1.5rem 1.5rem 0;
`

export type AssigneeInfo = AssigneeInput & {
  name: string
}

/**
 * For intialising a form
 */
export const convertToAssigneeInfo = (
  assignees: AssigneeLike[]
): AssigneeInfo[] => {
  const formattedAssignees: AssigneeInfo[] = []

  assignees?.forEach((assignee) => {
    const name = getAssigneeName(assignee)[0]

    if (assignee.worker) {
      formattedAssignees.push({
        id: assignee.worker.id,
        type: AssigneeType.Worker,
        name,
      })
    } else if (assignee.subcontractor) {
      formattedAssignees.push({
        id: assignee.subcontractor.id,
        type: AssigneeType.Subcontractor,
        name,
      })
    } else if (assignee.adminUser) {
      formattedAssignees.push({
        id: assignee.adminUser.id,
        type: AssigneeType.AdminUser,
        name,
      })
    }
  })

  return formattedAssignees
}

/**
 * For saving back to GraphQL
 */
export const convertToAssigneeInput = (
  assignees: AssigneeInfo[]
): AssigneeInput[] => {
  return assignees?.map(({ id, type }) => ({ id, type }))
}

export const convertToAssigneeNames = (assignees: AssigneeLike[]): string => {
  return assignees?.map((a) => getAssigneeName(a)[0]).join(', ')
}

const adminUsersColumns = () => [
  {
    title: 'Name',
    dataIndex: 'name',
  },
  {
    title: 'Email',
    dataIndex: 'email',
  },
]

const workersColumns = () => [
  {
    title: 'Name',
    dataIndex: 'name',
  },
  {
    title: 'Trade',
    dataIndex: 'trade',
  },
  {
    title: 'Mobile',
    dataIndex: 'mobile',
  },
  {
    title: 'Email',
    dataIndex: 'email',
  },
]

const createSubbieContactLink =
  (router: NextRouter) => (subcontractorId: string) => {
    router.push(
      routes.subcontractors.view(subcontractorId, { tab: 'contacts' })
    )
  }

const createAddNewContactLink =
  (router: NextRouter) => (subcontractorId: string) => {
    router.push(routes.subcontractors.newContact(subcontractorId))
  }

const createEditContactLink =
  (router: NextRouter) => (subcontractorId: string, contactId: string) => {
    router.push(routes.subcontractors.editContact(subcontractorId, contactId))
  }

const subbiesColumns =
  (
    addNewContactLink: (subcontractorId: string) => void,
    editContactLink: (subcontractorId: string, contactId: string) => void,
    subbieContactsLink: (subcontractorId: string) => void
  ) =>
  () => [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Primary Contact',
      dataIndex: 'contacts',
      render: (contacts: Contact[], record: Subcontractor) => (
        <ContactList
          onClickEditContact={(contactId: string) =>
            editContactLink(record?.id, contactId)
          }
          onClickAddContacts={() => addNewContactLink(record?.id)}
          onClickViewContacts={() => subbieContactsLink(record?.id)}
          contacts={contacts}
          showMobile={false}
          showAllProjectContacts={false}
        />
      ),
    },
  ]

type CachedWorkerData = WorkersListForSearchUiQuery['workers']
type CachedAdminData = AdminUsersForSearchUiQuery['adminUsers']
type CachedSubbieData = Array<{ id: string; name: string; contacts: Contact[] }>

type Props = {
  value?: AssigneeInfo[]
  onChange?: (value: AssigneeInfo[]) => void
  children?: (
    value: AssigneeInfo[],
    open: () => void,
    onClear: (id: string) => void
  ) => React.ReactNode
  showAdminUsers?: boolean
  showSubcontractors?: boolean
  showWorkers?: boolean
  singleSelect?: boolean
  showStaff?: boolean
  project?: ProjectLike
  includeBuilderSubcontractor?: boolean
  onSave?: (value: AssigneeInfo[]) => void
  buttonProps?: ButtonProps
  buttonText?: string
}

export const AssigneeSelect: React.FC<Props> = ({
  value = [],
  onChange,
  project,
  showAdminUsers = true,
  showSubcontractors = true,
  showWorkers = true,
  singleSelect = false,
  showStaff = false,
  includeBuilderSubcontractor = true,
  children,
  onSave = undefined,
  buttonProps,
  buttonText,
}) => {
  const router = useRouter()

  const { builder } = useUser() ?? {}

  const [modalOpen, toggleModal] = useState(false)
  const [currentChanges, setCurrentChanges] = useState<AssigneeInfo[]>(value) // so changes aren't applied until modal is submitted
  const [onlyShowProjectSubbies, setOnlyShowProjectSubbies] = useState(
    Boolean(project)
  )

  const openModal = () => toggleModal(true)
  const closeModal = () => toggleModal(false)

  const onDone = () => {
    onChange?.(currentChanges)

    if (onSave) {
      onSave(currentChanges)
    }

    closeModal()
  }

  const onCancel = () => {
    setCurrentChanges(value) // reset changes
    closeModal()
  }

  const [cachedAdminData, setCachedAdminData] = useState<CachedAdminData>([])
  const [cachedWorkerData, setCachedWorkerData] = useState<CachedWorkerData>([])
  const [cachedSubbieData, setCachedSubbieData] = useState<CachedSubbieData>([])

  const getAssigneeValue = (type: AssigneeType, id: string): AssigneeInfo => {
    if (type === AssigneeType.Worker) {
      const worker = cachedWorkerData.find((w) => w.id === id)
      if (!worker) return undefined

      return {
        id: worker.id,
        type: AssigneeType.Worker,
        name: `${worker.user.firstName} ${worker.user.lastName}`,
      }
    }
    if (type === AssigneeType.Subcontractor) {
      const subbie = cachedSubbieData.find((s) => s.id === id)
      if (!subbie) return undefined

      return {
        id: subbie.id,
        type: AssigneeType.Subcontractor,
        name: subbie.name,
      }
    }
    if (type === AssigneeType.AdminUser) {
      const adminUser = cachedAdminData.find((a) => a.id === id)
      if (!adminUser) return undefined

      return {
        id: adminUser.id,
        type: AssigneeType.AdminUser,
        name: `${adminUser.user.firstName} ${adminUser.user.lastName}`,
      }
    }
  }

  const onRowSelection =
    (type: AssigneeType) => (selectedRowKeys: string[]) => {
      if (singleSelect) {
        const selected = selectedRowKeys[selectedRowKeys.length - 1]
        setCurrentChanges([getAssigneeValue(type, selected)].filter(Boolean))
        return
      }

      const strippedValue = currentChanges.filter((a) => a.type !== type)
      const assigneesOfType = selectedRowKeys.map((id) =>
        getAssigneeValue(type, id)
      )

      setCurrentChanges([...strippedValue, ...assigneesOfType].filter(Boolean))
    }

  const onClear = (id: string) => {
    setCurrentChanges(currentChanges.filter((a) => a.id !== id))
  }

  const onHardRemove = (id: string) => {
    const newValue = currentChanges.filter((a) => a.id !== id)
    setCurrentChanges(newValue)
    onChange?.(newValue)
  }

  const selectedAdminUsers = useMemo(() => {
    return currentChanges
      ?.filter((a) => a.type === AssigneeType.AdminUser)
      .map((a) => a.id)
  }, [currentChanges])

  const selectedWorkers = useMemo(() => {
    return currentChanges
      ?.filter((a) => a.type === AssigneeType.Worker)
      .map((a) => a.id)
  }, [currentChanges])

  const selectedSubbies = useMemo(() => {
    return currentChanges
      ?.filter((a) => a.type === AssigneeType.Subcontractor)
      .map((a) => a.id)
  }, [currentChanges])

  const handleAdminUserData = (
    data: AdminUsersForSearchUiQuery['adminUsers']
  ) => {
    setCachedAdminData([
      ...(cachedAdminData || []),
      ...(data?.filter((d) => !cachedAdminData?.some((c) => c.id === d.id)) ||
        []),
    ])
  }

  const handleWorkerData = (
    data:
      | WorkersListForSearchUiQuery['workers']
      | BuilderWorkersUiQuery['builderWorkers']
  ) => {
    setCachedWorkerData([
      ...(cachedWorkerData || []),
      ...(data?.filter((d) => !cachedWorkerData?.some((c) => c.id === d.id)) ||
        []),
    ] as CachedWorkerData)
  }

  const handleSubbieData = (data) => {
    setCachedSubbieData([
      ...(cachedSubbieData || []),
      ...(data?.filter((d) => !cachedSubbieData?.some((c) => c.id === d.id)) ||
        []),
    ])
  }

  const tabs = useMemo<Partial<Record<keyof typeof Tab, string>>>(() => {
    return {
      ...(showAdminUsers && { AdminUsers: Tab.AdminUsers }),
      ...(showStaff && {
        Staff: `${builder?.company?.name ?? 'Builder'} workers`,
      }),
      ...(showSubcontractors && { Subbies: Tab.Subbies }),
      ...(showWorkers && { Workers: Tab.Workers }),
    }
  }, [
    builder?.company?.name,
    showAdminUsers,
    showStaff,
    showSubcontractors,
    showWorkers,
  ])

  return (
    <>
      {children ? (
        children(value, openModal, onHardRemove)
      ) : (
        <Button onClick={openModal} {...buttonProps}>
          Select {pluraliseWord(singleSelect ? 1 : 0, 'assignee')}{' '}
          {value?.length ? `(${value.length})` : ''}
        </Button>
      )}

      <Modal
        width={buildPassTheme.modal.medium}
        open={modalOpen}
        onCancel={onCancel}
        noPadding
        footerLayout="split"
        footer={
          <>
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="primary" onClick={onDone}>
              {buttonText ??
                'Save ' + pluraliseWord(currentChanges.length, 'assignee')}
            </Button>
          </>
        }
        centered
      >
        <ContentWrapper>
          <>
            <Text variant="heading04" mb="0.5rem">
              Select Assignees
            </Text>

            <Text variant="label02" strong>
              Current assignees:
            </Text>
            <Flexbox flexWrap="wrap" gap="0.375rem" mb="1rem">
              {!currentChanges.length && (
                <Text secondary="_400" variant="caption01">
                  No assignees selected
                </Text>
              )}
              {currentChanges?.map((assignee) => (
                <AssigneePill
                  key={assignee.id}
                  id={assignee.id}
                  name={assignee.name}
                  type={assignee.type}
                  onClear={onClear}
                />
              ))}
            </Flexbox>
          </>

          <Tabs
            green
            tabs={tabs}
            info={{
              AdminUsers: selectedAdminUsers?.length,
              Staff: selectedWorkers?.length,
              Subbies: selectedSubbies?.length,
              Workers: selectedWorkers?.length,
            }}
          >
            {(activeTab) => (
              <TabContent style={{ minHeight: '50vh' }}>
                {activeTab === 'AdminUsers' && (
                  <ServerSideListView<
                    AdminUsersForSearchUiQuery,
                    AdminUsersForSearchUiQueryVariables
                  >
                    bordered={false}
                    breakpoint={20}
                    defaultPageSize={20}
                    ignoreRouteFilters
                    aggregationIndex={'adminUsersAggregation'}
                    resource="Admin users"
                    query={GET_ADMIN_USERS_FOR_SEARCH}
                    setDataCallback={handleAdminUserData}
                    variables={{
                      filter: { archivedFilter: ArchivedFilter.ActiveOnly },
                    }}
                    dataTransformer={(data: AdminUsersForSearchUiQuery) =>
                      data?.adminUsers.map((adminUser) => ({
                        name: `${adminUser.user.firstName} ${adminUser.user.lastName}`,
                        email: adminUser.user.email,
                        ...adminUser,
                      }))
                    }
                    getColumns={adminUsersColumns}
                    customEmptyComponent={
                      <>
                        <IconMoodSad size={24} />
                        <p>No data found</p>
                      </>
                    }
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedAdminUsers,
                      onChange: onRowSelection(AssigneeType.AdminUser),
                      preserveSelectedRowKeys: true,
                      hideSelectAll: singleSelect,
                    }}
                    searchable
                    includeReset={false}
                    scroll={{ y: '30vh' }}
                  />
                )}

                {activeTab === 'Staff' && (
                  <ServerSideListView<
                    BuilderWorkersUiQuery,
                    BuilderWorkersUiQueryVariables
                  >
                    bordered={false}
                    breakpoint={20}
                    defaultPageSize={20}
                    ignoreRouteFilters
                    aggregationIndex={'builderWorkersAggregation'}
                    resource="Workers"
                    query={GET_STAFF_WORKERS}
                    variables={{ filter: { projectId: project?.id } }}
                    setDataCallback={handleWorkerData}
                    dataTransformer={(data: BuilderWorkersUiQuery) =>
                      data?.builderWorkers.map((worker) => ({
                        name: getWorkerName(worker),
                        email: worker.user.email,
                        mobile: worker.mobile,
                        ...worker,
                      }))
                    }
                    getColumns={workersColumns}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedWorkers,
                      onChange: onRowSelection(AssigneeType.Worker),
                      preserveSelectedRowKeys: true,
                      hideSelectAll: singleSelect,
                    }}
                    searchable
                    includeReset={false}
                    scroll={{ y: '30vh' }}
                  />
                )}

                {activeTab === 'Workers' && (
                  <ServerSideListView<
                    WorkersListForSearchUiQuery,
                    WorkersListForSearchUiQueryVariables
                  >
                    bordered={false}
                    breakpoint={20}
                    defaultPageSize={20}
                    ignoreRouteFilters
                    aggregationIndex={'workersAggregation'}
                    resource="Workers"
                    query={GET_WORKERS_FOR_SEARCH}
                    setDataCallback={handleWorkerData}
                    variables={{
                      filter: {
                        shouldIncludeBuilderSubcontractors: !showStaff,
                        shouldIncludeManuallyGeneratedWorkers: true,
                      },
                    }}
                    dataTransformer={(data: WorkersListForSearchUiQuery) =>
                      data?.workers.map((worker) => ({
                        name: getWorkerName(worker),
                        email: worker.user.email,
                        mobile: worker.mobile,
                        ...worker,
                      }))
                    }
                    getColumns={workersColumns}
                    rowSelection={{
                      type: 'checkbox',
                      selectedRowKeys: selectedWorkers,
                      onChange: onRowSelection(AssigneeType.Worker),
                      preserveSelectedRowKeys: true,
                      hideSelectAll: singleSelect,
                    }}
                    searchable
                    includeReset={false}
                    scroll={{ y: '30vh' }}
                  />
                )}

                {activeTab === 'Subbies' && (
                  <>
                    {project && (
                      <Flexbox alignItems="center" gap="0.5rem" mb="1.5rem">
                        <Switch
                          checked={onlyShowProjectSubbies}
                          onChange={(checked) =>
                            setOnlyShowProjectSubbies(checked)
                          }
                        />{' '}
                        <Text secondary="_500">
                          Only show subcontractors assigned to {project.name}
                        </Text>
                      </Flexbox>
                    )}
                    {onlyShowProjectSubbies && (
                      <ServerSideListView<
                        ProjectSubbieAssigneesUiQuery,
                        ProjectSubbieAssigneesUiQueryVariables
                      >
                        bordered={false}
                        breakpoint={20}
                        defaultPageSize={20}
                        ignoreRouteFilters
                        aggregationIndex={'subcontractorsByProjectAggregation'}
                        resource="Subcontractors"
                        query={GET_PROJECT_SUBBIES_ASSIGNEES}
                        setDataCallback={handleSubbieData}
                        customEmptyComponent={
                          <Button
                            variant="primary-text"
                            onClick={() =>
                              setOnlyShowProjectSubbies(!onlyShowProjectSubbies)
                            }
                          >
                            Search all Projects
                          </Button>
                        }
                        variables={{
                          projectId: project?.id,
                          filter: {
                            includeBuilderSubcontractor,
                          },
                        }}
                        dataTransformer={(
                          data: ProjectSubbieAssigneesUiQuery
                        ) =>
                          data?.subcontractorsByProject.map((s) => ({
                            id: s.id,
                            name: s.company.name,
                            contacts: s.contacts,
                          }))
                        }
                        getColumns={subbiesColumns(
                          createAddNewContactLink(router),
                          createEditContactLink(router),
                          createSubbieContactLink(router)
                        )}
                        rowSelection={{
                          type: 'checkbox',
                          selectedRowKeys: selectedSubbies,
                          onChange: onRowSelection(AssigneeType.Subcontractor),
                          preserveSelectedRowKeys: true,
                          hideSelectAll: singleSelect,
                        }}
                        searchable
                        includeReset={false}
                        scroll={{ y: '30vh' }}
                      />
                    )}
                    {!onlyShowProjectSubbies && (
                      <ServerSideListView<
                        BuilderSubbieAssigneesUiQuery,
                        BuilderSubbieAssigneesUiQueryVariables
                      >
                        bordered={false}
                        breakpoint={20}
                        defaultPageSize={20}
                        ignoreRouteFilters
                        aggregationIndex={'subcontractorsByBuilderAggregation'}
                        resource="Subcontractors"
                        query={GET_BUILDER_SUBBIE_ASSIGNEES}
                        setDataCallback={handleSubbieData}
                        variables={{
                          filter: {
                            includeBuilderSubcontractor,
                          },
                        }}
                        dataTransformer={(
                          data: BuilderSubbieAssigneesUiQuery
                        ) =>
                          data?.subcontractorsByBuilder?.map((s) => ({
                            id: s.id,
                            name: s.company.name,
                            contacts: s.contacts,
                          }))
                        }
                        getColumns={subbiesColumns(
                          createAddNewContactLink(router),
                          createEditContactLink(router),
                          createSubbieContactLink(router)
                        )}
                        rowSelection={{
                          type: 'checkbox',
                          selectedRowKeys: selectedSubbies,
                          onChange: onRowSelection(AssigneeType.Subcontractor),
                          preserveSelectedRowKeys: true,
                        }}
                        searchable
                        includeReset={false}
                        scroll={{ y: '30vh' }}
                        customEmptyComponent={
                          <>
                            <IconMoodSad size={24} />
                            <p>No data found</p>
                          </>
                        }
                      />
                    )}
                  </>
                )}
              </TabContent>
            )}
          </Tabs>
        </ContentWrapper>
      </Modal>
    </>
  )
}
